export interface PendingPageView {
  url: string;
  // The page's :tenantId, else the app's tenant at navigation; null before any tenant is known.
  tenantId: string | null;
}

export interface ReadyPageView {
  url: string;
  tenantId: string;
  userId: string;
}

function sameTenant(a: string | null, b: string | null): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

// Holds page views until the app's tenant, the user loaded for it and the page's tenant all agree,
// so a view is never recorded under one tenant with another tenant's user or page.
export class PageViewGate {
  private pending: PendingPageView[] = [];
  private ready: ReadyPageView[] = [];
  private tenantId: string | null = null;
  private userId: string | null = null;
  private userTenantId: string | null = null;
  // Each user id belongs to the tenant that was current when it first loaded. A late emission of the
  // previous tenant's user, after a switch, stays tied to that tenant.
  private readonly userTenants = new Map<string, string>();

  tenantChanged(tenantId: string | null | undefined): void {
    const next = tenantId || null;
    if (next === this.tenantId || sameTenant(next, this.tenantId)) {
      return;
    }
    this.tenantId = next;
    this.bind();
  }

  userChanged(userId: string | null | undefined): void {
    this.userId = userId || null;
    if (this.userId && this.tenantId && !this.userTenants.has(this.userId)) {
      this.userTenants.set(this.userId, this.tenantId);
    }
    this.userTenantId = this.userId ? this.userTenants.get(this.userId) ?? null : null;
    this.bind();
  }

  navigated(url: string, routeTenantId: string | null | undefined): void {
    this.pending.push({ url, tenantId: routeTenantId || this.tenantId });
    this.bind();
  }

  take(): ReadyPageView[] {
    const ready = this.ready;
    this.ready = [];
    return ready;
  }

  clear(): void {
    this.pending = [];
    this.ready = [];
  }

  // A view is tied to its user as soon as both are known, so a later switch cannot drop it.
  // A view of another tenant waits until the app is on that tenant and its user has loaded.
  private bind(): void {
    const tenantId = this.tenantId;
    const userId = this.userId;
    if (!userId || !tenantId || !sameTenant(this.userTenantId, tenantId)) {
      return;
    }
    const bound = this.pending.filter(view => view.tenantId === null || sameTenant(view.tenantId, tenantId));
    this.pending = this.pending.filter(view => !bound.includes(view));
    this.ready.push(...bound.map(view => ({ url: view.url, tenantId, userId })));
  }
}
